'use client';
import React, { useRef, useEffect, memo } from 'react';
import defaultCss from './defaultCss';

// 定义组件属性类型
interface ShadowDomRichTextProps {
  // 传入的富文本内容
  richText: string;
  // 可选：Shadow DOM的模式(open/closed)，默认为open
  mode?: 'open' | 'closed';
  className?: string;
  style?: React.CSSProperties;
  // 是否使用Shadow DOM，默认为true
  isShadowDom?: boolean;
}

const wrapperClassName = '__shadow-dom-wrapper__';

const RichComponent: React.FC<ShadowDomRichTextProps> = ({
  richText = '',
  className = '',
  style = {},
}) => {
  return (
    <div style={style} className={className} dangerouslySetInnerHTML={{ __html: richText }}></div>
  );
};
/**
 * 创建带有Shadow DOM的组件，用于安全展示富文本内容
 * Shadow DOM可以隔离样式和DOM，防止富文本内容影响外部
 */
const ShadowDomRichText: React.FC<ShadowDomRichTextProps> = memo(
  ({ richText, mode = 'open', className = '', style = {}, isShadowDom = true }) => {
    // 用于获取容器元素的ref
    const containerRef = useRef<HTMLDivElement>(null);
    const shadowDomRef = useRef<ShadowRoot | null>(null);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // 清除已存在的Shadow DOM
      if (shadowDomRef.current) {
        shadowDomRef.current.innerHTML = '';
      }
      let wrapper = container.querySelector(`.${wrapperClassName}`);
      if (wrapper) {
        container.removeChild(wrapper);
      }
      wrapper = document.createElement('div');
      wrapper.className = wrapperClassName;
      container.appendChild(wrapper);
      // 创建Shadow DOM
      shadowDomRef.current = wrapper.attachShadow({ mode });

      // 使用dangerouslySetInnerHTML的原生实现
      shadowDomRef.current.innerHTML = richText ?? '';
      // 注入基础样式，防止富文本样式错乱
      // 这些样式可以根据需要进行调整
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(defaultCss);
      shadowDomRef.current.adoptedStyleSheets = [sheet];

      // 组件卸载时清理
      return () => {
        if (shadowDomRef.current) {
          shadowDomRef.current.innerHTML = '';
        }
      };
    }, [richText, mode]);

    if (!isShadowDom) {
      return <RichComponent richText={richText} className={className} style={style} />;
    }
    return (
      <div
        ref={containerRef}
        style={style}
        className={className}
        aria-label='rich text container'
      />
    );
  },
);

ShadowDomRichText.displayName = 'ShadowDomRichText';

export default ShadowDomRichText;
